import {ErrorWithUserMessage} from '@/model/errors/ErrorWithUserMessage';
import {computed, onBeforeUnmount, Ref, ref} from 'vue';

const MAX_PARALLEL_UPLOADS = 4;

export interface Upload<T> {
    key: number;
    file: File;
    /** Position the file was selected at, the server keeps it */
    position: number;
    previewUrl: string;
    state: 'waiting' | 'uploading' | 'done' | 'failed';
    result: T | null;
    error: string | null;
}

/**
 * Uploads files a few at a time. Each file carries the position it was selected at, so the files
 * keep the selected order whichever of them arrives at the server first.
 *
 * A file that the server saves as more items (the pages of a PDF) takes the positions up to the next file,
 * the positions of the files are the given step apart.
 */
export function useOrderedUploads<T>(
    upload: (file: File, position: number) => Promise<T>,
    onSettled: () => void,
    positionStep = 1,
) {
    const uploads = ref([]) as Ref<Upload<T>[]>;
    let nextKey = 0;
    let requestsInFlight = 0;

    const sortedUploads = computed(() => [...uploads.value].sort((a, b) => a.position - b.position));
    const isBusy = computed(() => uploads.value.some(item => item.state === 'waiting' || item.state === 'uploading'));
    const doneCount = computed(() => uploads.value.filter(item => item.state === 'done').length);
    const totalCount = computed(() => uploads.value.filter(item => item.state !== 'failed').length);

    function add(files: File[], firstPosition: number): void {
        uploads.value = [
            ...uploads.value,
            ...files.map((file, index): Upload<T> => ({
                key: nextKey++,
                file,
                position: firstPosition + index * positionStep,
                previewUrl: URL.createObjectURL(file),
                state: 'waiting',
                result: null,
                error: null,
            })),
        ];

        startWaiting();
    }

    /**
     * The first position after the saved items and the files still being uploaded
     */
    function nextPosition(savedPositions: number[]): number {
        const positions = [
            ...savedPositions.map(position => position + 1),
            ...uploads.value.map(item => item.position + positionStep),
        ];

        return positions.length === 0 ? 0 : Math.max(...positions);
    }

    function retry(key: number): void {
        const item = find(key);

        if (item === undefined) {
            return;
        }

        item.state = 'waiting';
        item.error = null;

        startWaiting();
    }

    function discard(key: number): void {
        const item = find(key);

        if (item !== undefined) {
            URL.revokeObjectURL(item.previewUrl);
        }

        uploads.value = uploads.value.filter(current => current.key !== key);
    }

    /**
     * Forgets the uploaded files the list of saved items already shows
     */
    function clearShown(isShown: (result: T) => boolean): void {
        const shown = (item: Upload<T>) => item.state === 'done' && isShown(item.result as T);

        uploads.value.filter(shown).forEach(item => URL.revokeObjectURL(item.previewUrl));

        uploads.value = uploads.value.filter(item => ! shown(item));
    }

    function startWaiting(): void {
        let running = uploads.value.filter(item => item.state === 'uploading').length;

        for (const item of sortedUploads.value) {
            if (running >= MAX_PARALLEL_UPLOADS) {
                return;
            }

            if (item.state === 'waiting') {
                start(item.key);
                running++;
            }
        }
    }

    function start(key: number): void {
        const item = find(key)!;

        item.state = 'uploading';
        requestsInFlight++;

        upload(item.file, item.position)
            .then(result => {
                item.state = 'done';
                item.result = result;
            })
            .catch(error => {
                item.state = 'failed';
                item.error = error instanceof ErrorWithUserMessage
                    ? error.message
                    : 'Soubor se nepodařilo nahrát.';
            })
            .finally(() => {
                requestsInFlight--;

                startWaiting();

                if (requestsInFlight === 0) {
                    onSettled();
                }
            });
    }

    function find(key: number): Upload<T> | undefined {
        return uploads.value.find(item => item.key === key);
    }

    onBeforeUnmount(() => uploads.value.forEach(item => URL.revokeObjectURL(item.previewUrl)));

    return {
        uploads: sortedUploads,
        isBusy,
        doneCount,
        totalCount,
        add,
        nextPosition,
        retry,
        discard,
        clearShown,
    };
}
